import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from 'moment';
import { Button, Form } from "react-bootstrap";
import { getUser } from "../../../../services/user";
import { AdminUserNew } from "./user-new";
import { Club } from "../../../../interfaces/club";
import { getClubOptions } from "../../../../services/club";
import { AdminUserEdit } from "./user-edit";
import { AdminUserDelete } from "./user-delete";
import { User } from "../../../../interfaces/user";
import { roleOptions } from "../../../../data/roles";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../App";
import { getPaginationOptions, tableStyles } from "../../../../util/data-table";

export function AdminUserList() {
    const navigate = useNavigate();

    const { userProfile } = useContext(UserContext);

    const [users, setUsers] = useState<User[]>([]);
    const [clubOptions, setClubOptions] = useState<any[]>([]);
    const [showAddNew, setShowAddNew] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const CustomName = (row: any ) => {
        return (
            <span>
                <span>{ row.row.name }</span>
                { row.row.status == 0 && <span className="red"> ({ t("disabled") })</span> }
            </span>
        );
    };
    
    const CustomDate = (row: any ) => (
        <span>
            { moment(row.row.changed_at).format("DD/MM/YYYY HH:mm") }
        </span>
    );

    const CustomRole = (row: any ) => (
        <span>
            { row.row.role.map((r: any) => {
                return r.name
            }).join(', ') }
        </span>
    );

    const CustomClub = (row: any ) => (
        <span>
            { row.row.club.map((r: any) => {
                return r.name
            }).join(', ') }
        </span>
    );
    
    const CustomActions = (row: any) => (
        <span>
            <a href="#" className="icon-link edit" title={ t("edit") } onClick={ handleEdit } data-id={ row.row.id }>
                <i className="bi bi-pencil-square"></i>
            </a>
            { row.row.id != userProfile.id &&
                <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ row.row.id }>
                    <i className="bi bi-trash"></i>
                </a>
            }  
        </span>
    );

    const columns = [
        {
            id: 'id',
            name: '#',
            maxWidth: '80px',
            selector: (row: any) => row.id,
            sortable: true
        },
        {
            id: 'name',
            name: t("Name"),
            selector: (row: any) => row.name,
            sortable: true,
            cell: (row: any) => <CustomName row={row} />,
        },
        {
            id: 'email',
            name: t('email'),
            selector: (row: any) => row.email,
            sortable: true
        },
        {
            id: 'role',
            name: t('role'),
            selector: (row: any) => row.role,
            sortable: true,
            cell: (row: any) => <CustomRole row={row} />,
        },{
            id: 'club',
            name: t('club'),
            selector: (row: any) => row.club,
            sortable: true,
            cell: (row: any) => <CustomClub row={row} />,
        },
        {
            id: 'changed_at',
            name: t('changed_at'),
            selector: (row: any) => row.changed_at,
            sortable: true,
            cell: (row: any) => <CustomDate row={row} />,
        },
        {
            id: 'actions',
            name: t("actions"),
            maxWidth: '80px',
            cell: (row: any) => <CustomActions row={row} />,
        }

    ];

    useEffect(() => {
        Promise.allSettled([
            getClubOptions(),
            getUser()
        ]).then(response => {
            if (response[0].status == 'fulfilled') {
                setClubOptions(response[0].value.data);
                setLoaded(true);    
            } else {
                alert(response[0].reason);
            }
            if (response[1].status == 'fulfilled') {
                setUsers(response[1].value.data);
                setLoaded(true);    
            } else {
                alert(response[1].reason);
            }
        }).catch(error => {
            alert(error.message);
            navigate("/");
        });
    }, []);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const club = event.target.club.value;
        const role = event.target.role.value;

        getUser(s, club, role)
            .then(response => {
                setUsers(response.data);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAddNew(true)
    }

    const handleAddNewOnSave = (s: User) => {
        const newUsers = [
            s,
            ...users
        ];
        setUsers(newUsers);
        setShowAddNew(false);
    }

    const handleAddNewOnClose = () => {
        setShowAddNew(false);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDeleteOnDelete = (id: number) => {
        setSelectedId(0);
        const newUsers = users.filter((m: User) => m.id != id);
        setUsers(newUsers);
        setShowDelete(false);
    }

    const handleDeleteOnClose = () => {
        setSelectedId(0);
        setShowDelete(false);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedUser: User) => {
        setSelectedId(0);
        const newUsers = users.map((m: User) => {
            if (m.id == changedUser.id) {
                return changedUser;
            } else {
                return m;
            }
        });
        setUsers(newUsers);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    return (
        <div className="users-list-container">
            <h1>
                { t('users')}
            </h1>
            { !loaded && <LoadingSpinner />}
            { loaded &&
                <div className="container-inner">
                    <div className="filters-container">
                        <Form onSubmit={ handleSearchSubmit }>
                            <Form.Group className="field-search" controlId="search">
                                <Form.Control type="text" placeholder={ t("Write to search") } />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="club_id">
                                <Form.Select aria-label={ t("club") } name="club">
                                    <option value="">{ t("select_club") }</option>
                                    { clubOptions.map((c: Club) => {
                                        return <option key={ 'club-' + c.id } value={ c.id }>{ c.name }</option>;
                                    })}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="role">
                                <Form.Select aria-label={ t("role") } name="role">
                                    <option value="">{ t("select_role") }</option>
                                    { roleOptions.map((r: any) => {
                                        return <option key={ 'role-' + r.value } value={r.value}>{ t(r.label) }</option>
                                    })}
                                </Form.Select>
                            </Form.Group>
                            <div className="actions">
                                <Button variant="primary" type="submit">
                                    { t("search") }
                                </Button>
                            </div>
                        </Form>
                    </div>
                    <div className="table-container">
                        <DataTable
                            columns={ columns }
                            data={users}
                            // selectableRows={ true }
                            // selectableRowsHighlight={ true }
                            responsive={ true }
                            striped={ true }
                            highlightOnHover={ true }
                            defaultSortFieldId={ 'changed_at' }
                            defaultSortAsc={ false }
                            pagination={ true }
                            customStyles={ tableStyles }
                            paginationComponentOptions={ getPaginationOptions() }
                        />
                    </div>            
                    <a className="admin-add-new" href="#" onClick={ handleAddNew }>
                        <i className="bi bi-plus-circle"></i>
                    </a>
                    { showAddNew &&
                        <AdminUserNew onSave={ handleAddNewOnSave } onClose={ handleAddNewOnClose } />
                    }
                    { showEdit && (selectedId != 0) &&
                        <AdminUserEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
                    }
                    { showDelete && (selectedId != 0) &&
                        <AdminUserDelete id={ selectedId } onDelete={ handleDeleteOnDelete } onClose={ handleDeleteOnClose } />
                    }
                </div>
            }                 
        </div>  
    )
}